import { useState, useEffect, useRef } from 'react';
import Modal from '../shared/Modal';
import { upsertBlock, upsertAssignment, getBlockTemplates } from '../../utils/api';

const DEFAULT_DATE = `${new Date().getFullYear()}-09-01`;

function getDayFromDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' });
  } catch { return ''; }
}

// ── On-brand template dropdown ────────────────────────────────────────────────

function TemplateSelect({ templates, selectedId, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const selectedTpl = templates.find(t => t.id === selectedId) || null;
  const label = selectedId === null
    ? 'No template'
    : selectedTpl?.name || 'Select template…';

  const slots = selectedTpl
    ? selectedTpl.positions.reduce((s, p) => s + (p.qty || 1), 0)
    : 0;
  const preview = selectedTpl
    ? selectedTpl.positions.map(p => `${p.label}${p.qty > 1 ? ' ×' + p.qty : ''}`).join(' · ')
    : '';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        disabled={loading}
        style={{
          width: '100%', textAlign: 'left',
          fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700,
          padding: '9px 12px', borderRadius: 'var(--radius)',
          border: `1px solid ${selectedId !== null ? 'var(--black)' : 'var(--border)'}`,
          background: selectedId !== null ? 'var(--black)' : 'var(--off)',
          color: selectedId !== null ? 'white' : 'var(--mid)',
          cursor: loading ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        }}
      >
        <span>{loading ? 'Loading templates…' : label}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
          <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Selected template preview */}
      {selectedTpl && (
        <div style={{ marginTop: 6, fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)' }}>
          {slots} position{slots !== 1 ? 's' : ''}{preview ? ` · ${preview}` : ''}
        </div>
      )}
      {selectedId === null && !loading && (
        <div style={{ marginTop: 6, fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)' }}>
          Block will start empty — add positions manually.
        </div>
      )}

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 300,
          background: 'var(--white)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', boxShadow: '0 8px 24px rgba(0,0,0,0.13)',
          overflow: 'hidden',
        }}>
          {/* No template option */}
          {[{ id: null, name: 'No template', positions: [] }, ...templates].map(opt => {
            const isNoTemplate = opt.id === null;
            const isActive = selectedId === opt.id;
            const optSlots = opt.positions.reduce((s, p) => s + (p.qty || 1), 0);
            const optPreview = opt.positions.map(p => `${p.label}${p.qty > 1 ? ' ×' + p.qty : ''}`).join(' · ');
            return (
              <div
                key={opt.id ?? '__none'}
                onClick={() => { onChange(opt.id); setOpen(false); }}
                style={{
                  padding: '10px 14px', cursor: 'pointer',
                  background: isActive ? 'var(--off)' : 'var(--white)',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--off)'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'var(--white)'; }}
              >
                <span style={{ width: 14, paddingTop: 2, flexShrink: 0 }}>
                  {isActive && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M1.5 6L4.5 9L10.5 3" stroke="var(--black)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
                    {opt.name}
                    {!isNoTemplate && (
                      <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginLeft: 7 }}>
                        {optSlots} position{optSlots !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  {isNoTemplate ? (
                    <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 1 }}>
                      Start with an empty block
                    </div>
                  ) : optPreview ? (
                    <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {optPreview}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────

export default function AddBlockModal({ open, onClose, teamId, subRole, groupBlocks, staffLeads, onAdd, showToast }) {
  const [title, setTitle] = useState('');
  const [day, setDay] = useState(getDayFromDate(DEFAULT_DATE));
  const [date, setDate] = useState(DEFAULT_DATE);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  // Staff/Leads teams only: optional per-block all-meals grant. The block is a
  // NORMAL timed block either way — checking the box just adds the meal range.
  const [staffMeals, setStaffMeals] = useState(false);
  const [endDate, setEndDate] = useState(''); // all-meals range end (block date is the start)
  const [notes, setNotes] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  useEffect(() => {
    if (!open || !teamId) return;
    setLoadingTemplates(true);
    getBlockTemplates(teamId, subRole)
      .then(data => {
        const list = data || [];
        setTemplates(list);
        setSelectedTemplateId(list.length > 0 ? list[0].id : null);
      })
      .catch(() => { setTemplates([]); setSelectedTemplateId(null); })
      .finally(() => setLoadingTemplates(false));
  }, [open, teamId, subRole]);

  function handleDateChange(e) {
    const d = e.target.value;
    setDate(d);
    setDay(getDayFromDate(d));
  }

  function reset() {
    setTitle('');
    setDay(getDayFromDate(DEFAULT_DATE));
    setDate(DEFAULT_DATE);
    setStartTime(''); setEndTime(''); setStaffMeals(false); setEndDate(''); setNotes('');
    setTemplates([]); setSelectedTemplateId(null); setLoadingTemplates(false);
  }

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId) || null;
  const templatePositions = selectedTemplate?.positions || [];
  const totalSlots = templatePositions.reduce((s, p) => s + (p.qty || 1), 0);

  async function handleSave() {
    if (!title.trim()) { showToast('Block name is required.'); return; }
    if (!teamId) { showToast('No team selected.'); return; }
    // Times are mandatory for EVERY team (except group-block teams, which have
    // none by design): timeless blocks break conflict warnings and meal counts.
    if (!groupBlocks) {
      if (!date) { showToast('Date is required.'); return; }
      if (!startTime) { showToast('Start time is required.'); return; }
    }
    // Staff/Leads optional all-meals grant: needs an end date when checked.
    if (staffLeads && staffMeals) {
      if (!endDate) { showToast('Pick the all-meals end date (or uncheck the meals box).'); return; }
      if (endDate < date) { showToast('The meals end date must be on or after the block date.'); return; }
    }

    // Generate IDs client-side so we can update the UI before waiting for the server
    const blockId = crypto.randomUUID();
    const now = new Date().toISOString();
    const newBlock = {
      id: blockId, teamId,
      title: title.trim(),
      // Group-blocks teams: the block is a timeless named group — times live on
      // the team schedule items linked to it. Staff/Leads all-meals grant rides
      // along as endDate on an otherwise normal timed block.
      day: groupBlocks ? '' : day,
      date: groupBlocks ? '' : date,
      endDate: (staffLeads && staffMeals) ? endDate : '',
      startTime: groupBlocks ? '' : startTime,
      endTime: groupBlocks ? '' : endTime,
      notes: notes.trim(), colorClass: '', subRole: subRole || '',
      createdAt: now,
    };

    const newAssignments = [];
    if (selectedTemplate && templatePositions.length > 0) {
      for (const pos of templatePositions) {
        const qty = Math.max(1, pos.qty || 1);
        for (let i = 0; i < qty; i++) {
          newAssignments.push({
            id: crypto.randomUUID(),
            blockId, teamId,
            personId: '', positionLabel: pos.label,
            status: 'unsent', notes: '', sentAt: '', respondedAt: '',
            createdAt: now,
            // Explicit order: the parallel saves below land in racing row order,
            // so without this the display order scrambles.
            order: newAssignments.length,
          });
        }
      }
    }

    // Optimistic: update UI and close immediately — no waiting
    if (onAdd) onAdd(newBlock, newAssignments);
    if (newAssignments.length > 0) {
      showToast(`Block added with ${totalSlots} position${totalSlots !== 1 ? 's' : ''} from "${selectedTemplate.name}".`);
    } else {
      showToast('Block added.');
    }
    reset();
    onClose();

    // Persist to server in the background
    try {
      await upsertBlock(newBlock);
      if (newAssignments.length > 0) {
        await Promise.all(newAssignments.map(a => upsertAssignment(a)));
      }
    } catch (err) {
      showToast('Error saving block — please refresh.');
      console.error(err);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title="Add Block"
      footer={
        <>
          <button className="btn" onClick={() => { reset(); onClose(); }}>Cancel</button>
          <button className="btn btn-dark" onClick={handleSave}>Add Block</button>
        </>
      }
    >
      <div className="form-group">
        <label className="form-label">Block Name</label>
        <input
          className="form-input"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Thursday, Session 4, Saturday AM…"
        />
        <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 6 }}>
          {groupBlocks
            ? 'This team uses group blocks: just name the group (e.g. Vocal Team A) — no times here. Serving times come from team schedule items linked to this group.'
            : 'For day-based teams: use the day. For service-based teams: name it by service.'}
        </div>
      </div>
      {!groupBlocks && (
        <>
          <div className="form-group">
            <label className="form-label">Date</label>
            <input className="form-input" type="date" value={date} onChange={handleDateChange} />
            {day && (
              <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 5 }}>
                {day}
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="form-group">
            <div>
              <label className="form-label">Start Time</label>
              <input className="form-input" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} />
            </div>
            <div>
              <label className="form-label">End Time</label>
              <input className="form-input" type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
            </div>
          </div>
          {staffLeads && (
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: 'var(--font-b)', fontSize: 13 }}>
                <input type="checkbox" checked={staffMeals} onChange={e => setStaffMeals(e.target.checked)} />
                🍽 All meals for a date range (staff/leads)
              </label>
              {staffMeals && (
                <>
                  <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', margin: '6px 0' }}>
                    Everyone assigned to this block gets EVERY meal (breakfast, lunch, dinner) from the block date through:
                  </div>
                  <input className="form-input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ maxWidth: 220 }} />
                </>
              )}
            </div>
          )}
        </>
      )}
      <div className="form-group">
        <label className="form-label">Notes <span style={{ fontWeight: 400, color: 'var(--muted)' }}>(optional)</span></label>
        <input className="form-input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes for this block…" />
      </div>

      {/* Template selector */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
        <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>
          Template
        </div>

        {!loadingTemplates && templates.length === 0 ? (
          <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)' }}>
            No templates set — use <strong style={{ fontFamily: 'var(--font-h)', color: 'var(--black)' }}>Edit Block Templates</strong> on the roster page to create one.
          </div>
        ) : (
          <TemplateSelect
            templates={templates}
            selectedId={selectedTemplateId}
            onChange={setSelectedTemplateId}
            loading={loadingTemplates}
          />
        )}
      </div>
    </Modal>
  );
}
